// Third-party
import React from 'react';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';

// Types
import User from '@/types/user';
import ReducedComparison from '@/types/reduced-comparison';
import GenericContact from '@/types/generic-contact';
import Comparison from '@/types/comparison';

// Components
import { ErrorBoundary } from '../errors';
import DataTable from '../data/DataTable';
import ComparisonTableMenu from '../../containers/comparison/ComparisonTable/ComparisonTableMenu';
import { PrimaryLink } from '../buttons';
import { Tooltip } from '../tooltips';
import { ContactPastille } from '../misc';

// Utils
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { formatEventDate } from '@/utils/time';
import { getComparisonContactName, COMPARISON_TYPE, comparisonCreatedInPast, comparisonIsEditable } from '@/utils/comparison';
import { contactPastilleLight } from '@/utils/colors';

export enum ComparisonsTableFields {
  Name = 'NAME',
  SavedTo = 'SAVED_TO',
  DateCreated = 'DATE_CREATED',
  LastModified = 'LAST_MODIFIED',
  Rerun = 'RERUN',
  Open = 'OPEN',
  Actions = 'ACTIONS',
}

type Props = {
  user: User;
  compact?: boolean;
  fields: ComparisonsTableFields[];
  comparisons: ReducedComparison[];
  className?: string;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onOpenComparison: (comparison: ReducedComparison) => void;
  onRerunComparison: (comparison: ReducedComparison) => void;
  onDeleteComparison: (comparison: ReducedComparison) => void;
  onSaveComparison: (id: string, name: string, contact?: GenericContact) => Promise<Comparison>;
  scrollAncestor?: Window | HTMLElement | string;
  openingComparisonIds?: string[];
  rerunningComparisonIds?: string[];
  sortable?: boolean;
  responsive?: boolean;
  chevronOnHover?: boolean;
};

class LegacyComparisonsTable extends React.Component<Props> {
  static defaultProps = {
    onSaveComparison: () => Promise.resolve(),
    onRerunComparison: () => {},
    fields: [ComparisonsTableFields.Name],
  };

  // Update optimization to prevent the table from rendering if props are the same
  shouldComponentUpdate(prevProps: Props) {
    return (
      prevProps.comparisons !== this.props.comparisons ||
      !isEqual(prevProps.openingComparisonIds, this.props.openingComparisonIds) ||
      !isEqual(prevProps.rerunningComparisonIds, this.props.rerunningComparisonIds)
    );
  }

  getTableColumns = () => {
    const { chevronOnHover, fields, responsive, sortable, user, onDeleteComparison, onOpenComparison, onRerunComparison, onSaveComparison } = this.props;
    const headerClassName = sortable ? 'sortable' : '';

    const columns = [
      {
        Header: () => <span className={headerClassName}>{formatMessage('ComparisonsTable.Fields.Name')}</span>,
        className: 'cell-name',
        field: ComparisonsTableFields.Name,
        accessor: 'name',
        minWidth: 200,
        Cell: ({ value }: { value: string }) => (
          <Tooltip message={value} delayShow={300} data-private data-dd-privacy="mask">
            <span data-private data-dd-privacy="mask">
              {value}
            </span>
          </Tooltip>
        ),
      },
      {
        id: 'saved-to',
        field: ComparisonsTableFields.SavedTo,
        Header: () => <span className={headerClassName}>{formatMessage('ComparisonsTable.Fields.SavedTo')}</span>,
        className: 'light cell-contact',
        maxWidth: 200,
        Cell: ({ original }: { original: ReducedComparison }) => {
          switch (original.comparisonType) {
            case COMPARISON_TYPE.CONTACT: {
              const name = getComparisonContactName(original);

              let comparisonContact = original.contact;

              if (!comparisonContact?.fname && !comparisonContact?.lname && comparisonContact?.fullName) {
                const names = comparisonContact.fullName?.split(' ');
                comparisonContact = {
                  ...original.contact,
                  fname: names[0] || '',
                  lname: names[1] || '',
                };
              }

              return (
                <Tooltip message={name} delayShow={300} data-private data-dd-privacy="mask">
                  <span>
                    {comparisonContact && responsive && (
                      <ContactPastille
                        contact={comparisonContact}
                        backgroundColor={contactPastilleLight.bgColor}
                        borderColor={contactPastilleLight.borderColor}
                      />
                    )}
                    <span data-private data-dd-privacy="mask">
                      {name}
                    </span>
                  </span>
                </Tooltip>
              );
            }
            case COMPARISON_TYPE.COMPLIANCE: {
              const name = formatMessage('Common.Compliance');

              return (
                <Tooltip message={name} delayShow={300} data-private data-dd-privacy="mask">
                  <span data-private data-dd-privacy="mask">
                    {name}
                  </span>
                </Tooltip>
              );
            }
            default: {
              const message = formatMessage(`ComparisonsTable.${original.providedBy ? 'Shared' : 'Draft'}`);

              return (
                <Tooltip message={message} delayShow={300} data-private data-dd-privacy="mask">
                  <span className="empty-value">{message}</span>
                </Tooltip>
              );
            }
          }
        },
      },
      {
        Header: () => <span className={headerClassName}>{formatMessage('ComparisonsTable.Fields.DateCreated')}</span>,
        field: ComparisonsTableFields.DateCreated,
        id: 'date-created',
        className: 'light',
        width: 200,
        accessor: (comparison: ReducedComparison) =>
          // This being used only for Compliance comparisons, the `lastRunAt` field
          // gives us exactly what we want rather than going through the process of
          // updating the API DTOs. Should this be used somewhere else, we might want
          // to consider using the `createdAt` instead (not currently available from
          // the API responses).
          comparison.lastRunAt ? formatEventDate(comparison.lastRunAt) : '',
      },
      {
        Header: () => <span className={headerClassName}>{formatMessage('ComparisonsTable.Fields.LastModified')}</span>,
        field: ComparisonsTableFields.LastModified,
        id: 'last-modified',
        className: 'light cell-last-modified',
        width: 200,
        accessor: (comparison: ReducedComparison) => (comparison.lastRunAt ? formatEventDate(comparison.lastRunAt) : ''),
      },
      {
        field: ComparisonsTableFields.Open,
        width: !responsive ? 86 : 32,
        className: 'justify-content-center cell-open',
        Cell: ({ original }: { original: ReducedComparison }) => {
          const isLoading = !!original._id && this.props.openingComparisonIds?.includes(original._id);
          return (
            <PrimaryLink
              loading={isLoading}
              onClick={() => {
                onOpenComparison(original);
              }}
              className="d-flex justify-content-center"
            >
              {!isLoading && !responsive && <FormattedMessage id="ComparisonsTable.Open" />}
              {!isLoading && responsive && chevronOnHover && <i className="icon-cheveron-right" />}
            </PrimaryLink>
          );
        },
      },
      {
        field: ComparisonsTableFields.Rerun,
        width: 112,
        className: 'justify-content-center',
        Cell: ({ original }: { original: ReducedComparison }) => {
          const isLoading = !!original._id && this.props.rerunningComparisonIds?.includes(original._id);
          return (
            <PrimaryLink
              loading={isLoading}
              onClick={() => {
                onRerunComparison(original);
              }}
              disabled={!comparisonCreatedInPast(original) || !comparisonIsEditable(original)}
              className="d-flex justify-content-center"
            >
              {!isLoading && <FormattedMessage id="ComparisonsTable.Rerun" />}
            </PrimaryLink>
          );
        },
      },
      {
        width: !responsive ? 60 : 42,
        id: 'save-as',
        className: 'justify-content-end cell-icon',
        field: ComparisonsTableFields.Actions,
        accessor: (comparison: ReducedComparison) => (
          <ComparisonTableMenu user={user} comparison={comparison} onSaveComparison={onSaveComparison} onDeleteComparison={onDeleteComparison} />
        ),
      },
    ];

    return fields.map(field => columns.find(column => column.field === field)).filter(column => column != null);
  };

  render() {
    const { compact, className, comparisons, onLoadMore, hasMore, scrollAncestor, sortable } = this.props;

    return (
      <div className={classNames('comparisons-table d-flex flex-column', className)}>
        <ErrorBoundary>
          <DataTable
            scrollAncestor={scrollAncestor}
            singleSelection
            data-private
            data-dd-privacy="mask"
            columns={this.getTableColumns()}
            compact={compact}
            data={comparisons}
            noDataText={formatMessage('ComparisonsTable.NoRows')}
            onLoadMore={onLoadMore}
            showLoadMore={hasMore}
            sortable={sortable}
          />
        </ErrorBoundary>
      </div>
    );
  }
}

export default LegacyComparisonsTable;
